<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { BoxLayout } from '@shopware/composables'

interface ComponentProductCarouselProps {
    id?: string
    products: Schemas['Product'][]
    title?: string
    showTitle?: boolean
    layoutType?: BoxLayout
    showWishlist?: boolean
    showBadges?: boolean
    showSizes?: boolean
    showCheapestPrice?: boolean
    hideListingParentPrice?: boolean
    itemsPerSlide?: {
        default: number
        md?: number
        lg?: number
        xl?: number
    }
    gap?: number
    showNavigation?: 'inside' | 'outside' | '' | false
    showIndicators?: 'inside' | 'outside' | '' | false
    autoPlay?: boolean
    autoPlayInterval?: number
    loop?: boolean
    loading?: boolean
    skeletonItems?: number
    class?: string
}

const props = withDefaults(defineProps<ComponentProductCarouselProps>(), {
    id: '',
    title: '',
    showTitle: true,
    layoutType: 'standard',
    showWishlist: true,
    showBadges: true,
    showSizes: false,
    showCheapestPrice: true,
    hideListingParentPrice: true,
    itemsPerSlide: () => ({ default: 1, md: 2, lg: 3, xl: 4 }),
    gap: 10,
    showNavigation: 'inside',
    showIndicators: false,
    autoPlay: false,
    autoPlayInterval: 5000,
    loop: true,
    loading: false,
    skeletonItems: 4,
    class: ''
})

// Transform products to carousel items
const carouselItems = computed(() =>
    props.products.map((product) => ({
        id: product.id,
        data: product
    } as { id: string; data: Schemas['Product'] }))
)


const { t } = useI18n()
</script>

<template>
    <div
        :class="[
            'component-product-carousel md:px-1 lg:px-4',
            props.class
        ]"
    >
        <!-- Title -->
        <div
            v-if="showTitle && title"
        >
            <div class="text-xl font-bold text-primary md:-ml-1 lg:-ml-4">
                {{ title }}
            </div>
        </div>

        <!-- Skeleton Loading State -->
        <ComponentCarousel
            v-if="loading"
            :slides="Array.from({ length: skeletonItems }, (_, i) => ({ id: `skeleton-${i}` }))"
            :label="$t('carousel.label')"
            :breakpoints="{
                'default': { slidesPerView: 2.2 },
                sm: { slidesPerView: 3.2 },
                md: { slidesPerView: 3 },
                lg: { slidesPerView: 5 },
                xl: { slidesPerView: 5 },
                '2xl': { slidesPerView: 5 }
            }"
            :gap="gap"
            :enable-arrows="false"
            :enable-dots="false"
            :enable-autoplay="false"
            carousel-width="100%"
        >
            <template #default>
                <ComponentSkeleton preset="card" height="400px" />
            </template>
        </ComponentCarousel>

        <!-- Product Carousel -->
        <ComponentCarousel
            v-if="!loading"
            :id="id"
            ref="productSlider"
            :slides="carouselItems"
            :label="$t('carousel.label')"
            :breakpoints="{
                'default': {
                    slidesPerView: 2.2
                },
                sm: {
                    slidesPerView: 3.2
                },
                md: {
                    slidesPerView: 3
                },
                lg: {
                    slidesPerView: 5
                },
                xl: {
                    slidesPerView: 5
                },
                '2xl': {
                    slidesPerView: 5
                }
            }"
            :gap="gap"
            :enable-arrows="true"
            :enable-dots="false"
            :enable-autoplay="false"
            carousel-width="100%"
            left-arrow-class="btn btn-square bg-transparent z-40 border-none hidden md:flex absolute top-1/2 -translate-y-1/2 -left-6"
            right-arrow-class="btn btn-square bg-transparent z-40 border-none hidden md:flex absolute top-1/2 -translate-y-1/2 -right-6"
            dots-container-class="carousel__navdots flex justify-center gap-4 mt-4"
        >

            <template #left-arrow="{previous}">
                <FoundationButton
                    class="btn-square"
                    :aria-label="$t('carousel.previous')"
                    :aria-controls="id"
                    @click="previous()"
                >
                    <FoundationIcon name="chevron-left" size="xl" />
                </FoundationButton>
            </template>

            <template #right-arrow="{next}">
                <FoundationButton
                    class="btn-square"
                    :aria-label="$t('carousel.next')"
                    :aria-controls="id"
                    @click="next()"
                >
                    <FoundationIcon name="chevron-right" size="xl" />
                </FoundationButton>
            </template>

            <template #default="{ index }">
                <ProductCard
                    :product="(carouselItems?.[index]?.data as Schemas['Product'])"
                    :layout-type="layoutType"
                    :show-wishlist="showWishlist"
                    :show-badges="showBadges"
                    :show-sizes="showSizes"
                    :show-cheapest-price="showCheapestPrice"
                    :hide-listing-parent-price="hideListingParentPrice"
                    image-sizes="124px sm:197px md:171px lg:176px xl:227px 2xl:248px"
                    width="248"
                    height="188"
                    :position="index + 1"
                    :list-name="title || 'Product Carousel'"
                />
            </template>

            <template #empty>
                <div class="text-center py-8">
                    <p class="text-gray-500">
                        {{ t('product.noProductsFound') }}
                    </p>
                </div>
            </template>
        </ComponentCarousel>
    </div>
</template>
